import { h } from 'preact';
import Store from 'store/store';

import formatDateTime from 'utils/formatDateTime';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses, parseDate } from 'utils/utils';

import Button from 'components/button/button';
import EditorContent from 'components/editor/content';
import HubNavigation from 'components/editor/hubNavigation/hubNavigationFocus';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import CarouselSettings from 'components/editor/settings/carouselSettings/carouselSettings';
import LoadingBar from 'components/loadingBar/loadingBar';

import HeroCarousel from 'components/editor/pages/focus/landing/heroCarousel';
import UpcomingMovies from 'components/editor/pages/focus/landing/upcomingMovies';
import MoviesExplorer from 'components/editor/pages/focus/landing/moviesExplorer';
import { getDefaultLandingCtaText, getDefaultMovieLink } from 'components/editor/pages/focus/landing/utils';

import s from 'components/editor/pages/focus/landing.sss';

const NEW_AND_UPCOMING_CAROUSEL_LIMIT = 8;

export default class LandingEditor extends EditorContent {
	constructor(props) {
		super(props);

		this.state = {
			readyState: 'loading',
			movies: {},
		};

		this.resetItemCta = this.resetItemCta.bind(this);
	}

	componentWillMount() {
		super.componentWillMount?.();
		this.prepareData();
	}

	async prepareData() {
		await this.updateMovies();
		this.updateContent();
		this.setState({ readyState: 'loaded' });
	}

	async updateMovies() {
		await Store.get().publishedDataPromise;
		const movies = Store.get().publishedData?.movies || {};
		this.setState({ movies });
	}

	updateContent() {
		if (!this.props.dataRoot) return;
		const content = this.computeContent();
		jsonPointer.get(Store.get().data, jsonPointer.parse(this.props.dataRoot).concat('content'))?.reset(content);
	}

	computeContent() {
		const basePath = this.props.dataRoot || '';
		const liveData = jsonPointer.get(Store.get().data, basePath)?.content;
		const heroCarousel = (liveData?.hero?.list || []).filter(e => e.movie && this.state.movies[e.movie]);
		const sortedMovies = Object.values(this.state.movies).sort(this.sortMovies());

		return {
			carousels: { upcoming: { overrides: {} }, },
			hero: { list: heroCarousel },
			moviesOrder: Object.fromEntries(sortedMovies.map((movie, i) => [movie.id, i]))
		};
	}

	sortMovies() {
		const now = Date.now();
		const savedMovies = Store.get().savedData?.movies || {};

		const getStatusOrder = movie => [
			!movie?.placeholder && movie?.status?.theaters === 'inTheaters',
			!movie?.placeholder && movie?.status?.theaters === 'preSale',
			!movie?.placeholder && movie?.status?.theaters === 'comingSoon',
			!!movie?.placeholder,
			movie?.status?.homeEnt === 'homeEnt',
			true, // everything else goes last
		].indexOf(true);

		// Used published release date if available, otherwise fallback to saved release date (for placeholder films)
		const getReleaseDate = movie => parseDate(movie?.dates?.release) || parseDate(savedMovies[movie.id]?.content?.dates?.release) || null;

		return (a, b) => {
			// First, sort by status
			const statusOrderA = getStatusOrder(a);
			const statusOrderB = getStatusOrder(b);
			if (statusOrderA !== statusOrderB) return statusOrderA - statusOrderB;

			const releaseA = getReleaseDate(a);
			const releaseB = getReleaseDate(b);

			if (releaseA && releaseB && releaseA !== releaseB) {
				if (!releaseA) return 1;
				if (!releaseB) return -1;
				return Math.abs(now - releaseA) - Math.abs(now - releaseB);
			}

			// Finally, sort alphabetically
			return a.title.localeCompare(b.title);
		};
	}

	getUpcomingCarouselItems() {
		const today = formatDateTime(new Date(), 'yyyy-MM-dd');
		const content = this.props.content;
		return Object.values(this.state.movies)
			.sort(this.sortMovies())
			.reduce((acc, movie) => {
				if (movie.placeholder) return acc; // never include placeholders
				if (movie.dates?.hideFromCarouselsAfter && today >= movie.dates.hideFromCarouselsAfter) return acc;

				const theatersStatus = movie.status.theaters;
				const showInCarousel = theatersStatus === 'comingSoon' || theatersStatus === 'preSale' || theatersStatus === 'inTheaters';
				if (!showInCarousel) return acc;

				const pinned = content?.carousels?.upcoming?.pinned === movie.id;
				const overrides = content?.carousels?.upcoming?.overrides?.[movie.id];

				acc[pinned ? 'unshift' : 'push']({ movie, pinned, ...overrides });
				return acc;
			}, [])
			.slice(0, NEW_AND_UPCOMING_CAROUSEL_LIMIT); // limit to 8 items
	}

	async resetItemCta() {
		const ok = await confirm(
			'This will reset the selected carousel item',
			{ title: 'Reset carousel item?', danger: true, button: 'Reset' }
		);
		if (!ok) return;

		const basePath = `${this.props.dataRoot}/content${this.props.activeElement}`;
		jsonPointer.removeDeep(Store.get().data, `${basePath}/ctaText`);
		jsonPointer.removeDeep(Store.get().data, `${basePath}/link`);

		// Move out of the carousel item settings view
		this.props.select(this.props.activeElement.startsWith('/hero') ? '/hero' : '/carousels/upcoming');
	}

	renderVisual() {
		if (this.state.readyState === 'loading') {
			return (
				<div class={s.visual}>
					<HubNavigation />
					<LoadingBar class={s.loadingBar} />
				</div>
			);
		}

		const { activeElement, content } = this.props;
		const upcomingMovies = this.getUpcomingCarouselItems();
		const moviesArray = Object.values(this.state.movies || {}).sort(this.sortMovies());

		return (
			<div class={s.visual}>
				<HubNavigation />
				<SelectableSection section="/hero" class={s.page} {...this.props}>
					<HeroCarousel activeElement={activeElement} list={content.hero?.list} movies={this.state.movies} />
				</SelectableSection>
				<SelectableSection section="/carousels/upcoming" class={s.page} {...this.props}>
					<UpcomingMovies activeElement={activeElement} movies={upcomingMovies} />
				</SelectableSection>
				<div class={joinClasses(s.page, s.disabled)}>
					<MoviesExplorer movies={moviesArray} />
				</div>
			</div>
		);
	}

	renderSettingsHeroCarousel() {
		const selected = new Set(this.props.content?.hero?.list?.map(m => m.movie) || []);
		const filteredMovies = Object.fromEntries(
			Object.entries(this.state.movies).filter(([id, movie]) => {
				if (selected.has(id)) return true; // always include selected movies
				if (movie.placeholder) return false; // never include placeholders
				const status = movie.status;
				return status && (status.theaters !== 'unknown' || status.homeEnt === 'homeEnt');
			})
		);

		return (
			<CarouselSettings
				carouselId="hero"
				dataRoot={this.props.dataRoot}
				inCarouselLabel="In Hero Carousel"
				movies={filteredMovies}
				onItemSelect={element => this.props.select(element)}
				selectedMovies={this.props.content?.hero?.list}
				sortable
			/>
		);
	}

	renderSettingsUpcomingCarousel() {
		const movies = this.getUpcomingCarouselItems();
		const onItemSelect = section => {
			const movieId = section.split('/').pop();
			if (movieId) this.props.select(`/carousels/upcoming/overrides/${movieId}`);
		};

		return (
			<CarouselSettings
				carouselId="upcoming"
				dataRoot={this.props.dataRoot}
				movies={movies}
				onItemSelect={onItemSelect}
			/>
		);
	}

	renderCarouselItemSettings(movieData) {
		const basePath = `${this.props.dataRoot}/content${this.props.activeElement}`;
		const defaultCtaText = getDefaultLandingCtaText(movieData);
		const defaultLink = getDefaultMovieLink(movieData);
		const readonly = !canUser('write');
		const hasEdits = (
			!!jsonPointer.get(Store.get().data, `${basePath}/ctaText`) ||
			!!jsonPointer.get(Store.get().data, `${basePath}/link`)
		);

		// Key was added to ensure inputs reset properly when switching between items
		return (
			<div class={s.section}>
				<div class={s.sectionName}>{movieData.title || 'Item'}</div>
				<SimpleField
					dark
					disabled={readonly}
					key={`ctaText-${movieData.id}`}
					label="CTA text"
					path={`${basePath}/ctaText`}
					placeholder={defaultCtaText}
				/>
				<SimpleField
					dark
					disabled={readonly}
					key={`link-${movieData.id}`}
					label="Link"
					path={`${basePath}/link`}
					placeholder={defaultLink}
				/>
				<Button danger dark disabled={readonly || !hasEdits} onClick={this.resetItemCta} text>Reset CTA</Button>
			</div>
		);
	}

	renderSettings() {
		let content;
		const nav = [];

		const activeElement = this.props.activeElement;
		if (activeElement.startsWith('/hero')) {
			nav.push({ label: 'Hero Carousel', path: '/hero' });

			if (activeElement.startsWith('/hero/list')) {
				const item = jsonPointer.get(this.props.content, activeElement);
				const movieData = typeof item?.movie === 'string' ? this.state.movies?.[item.movie] : item?.movie;
				nav.push({ label: movieData?.content?.title || movieData?.title || 'Movie', path: activeElement });
				content = this.renderCarouselItemSettings(movieData);

			} else {
				content = this.renderSettingsHeroCarousel();
			}

		} else if (activeElement.startsWith('/carousels/upcoming')) {
			nav.push({ label: 'New & Upcoming Film Releases', path: '/carousels/upcoming' });

			if (activeElement.startsWith('/carousels/upcoming/overrides')) {
				const movieId = activeElement.split('/').pop();
				const movieData = this.state.movies?.[movieId];
				nav.push({ label: movieData?.content?.title || movieData?.title || 'Movie', path: activeElement });
				content = this.renderCarouselItemSettings(movieData);

			} else {
				content = this.renderSettingsUpcomingCarousel();
			}
		}

		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}